import { daysOfTheWeek, timeOptions } from "@plasma/data/time";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Label } from "../ui/label";
import { SelectCombobox } from "../ui/select-combobox";
import { Switch } from "../ui/switch";

interface BusinessHour {
  dayOfTheWeek: string;
  openTime: string | null;
  closeTime: string | null;
}

/**
 * The type this component expects - matches Step3Data from Location forms
 * Also compatible with the full Location type.
 */
export interface LocationBusinessHoursForm {
  businessHours: BusinessHour[];
  operationalStatus: string;
  closeDateRange: {
    closeStartDate: string | null;
    closeEndDate: string | null;
  };
}

function businessHoursErrorMessage(
  errors: Record<string, unknown> | undefined
): string | undefined {
  if (!errors?.businessHours || typeof errors.businessHours !== "object") {
    return undefined;
  }
  const businessHoursError = errors.businessHours;
  if (
    "message" in businessHoursError &&
    typeof businessHoursError.message === "string"
  ) {
    return businessHoursError.message;
  }
  if (
    "root" in businessHoursError &&
    businessHoursError.root &&
    typeof businessHoursError.root === "object" &&
    "message" in businessHoursError.root &&
    typeof businessHoursError.root.message === "string"
  ) {
    return businessHoursError.root.message;
  }
  return undefined;
}

/**
 * LocationBusinessHoursFields - Type-safe component for location business hours
 *
 * Must be rendered inside a react-hook-form `<Form>` provider whose values include
 * `businessHours`. Used by location create/edit flows.
 */
export function LocationBusinessHoursFields({
  showLabel = true,
}: {
  showLabel?: boolean;
}) {
  const form = useFormContext<LocationBusinessHoursForm>();

  const { fields, append } = useFieldArray({
    control: form.control,
    name: "businessHours",
  });

  const errorMessage = businessHoursErrorMessage(form.formState.errors);

  const fieldsWithIndex: Array<(typeof fields)[number] & { index: number }> =
    [];
  for (let index = 0; index < fields.length; index++) {
    const field = fields[index];
    if (field === undefined) {
      continue;
    }
    fieldsWithIndex.push({ ...field, index });
  }

  return (
    <div className="w-md space-y-5">
      {showLabel && <p className="label text-foreground">Business hours</p>}
      {daysOfTheWeek.map((day) => {
        const businessHours = fieldsWithIndex.filter(
          (f) => f.dayOfTheWeek === day
        );

        return (
          <div className="flex gap-4" key={day}>
            <div className="flex h-12 w-32 items-center space-x-2">
              <Switch
                checked={businessHours.length > 0}
                id={day}
                onCheckedChange={(value) => {
                  if (value) {
                    const currentValues = form.getValues("businessHours");
                    form.setValue("businessHours", [
                      ...currentValues,
                      {
                        dayOfTheWeek: day,
                        openTime: "12:00am",
                        closeTime: "12:30am",
                      },
                    ]);
                  } else {
                    const currentValues = form.getValues("businessHours");
                    form.setValue(
                      "businessHours",
                      currentValues.filter((f) => f.dayOfTheWeek !== day)
                    );
                  }
                }}
              />
              <Label
                className="body-sm text-foreground leading-tight"
                htmlFor={day}
              >
                {day}
              </Label>
            </div>
            <div className="space-y-4">
              {businessHours.map((businessHour, i) => {
                const startTimeWatch = form.watch(
                  `businessHours.${businessHour.index}.openTime`
                );
                const endTimeWatch = form.watch(
                  `businessHours.${businessHour.index}.closeTime`
                );
                const startTimeIndex = timeOptions.indexOf(
                  startTimeWatch ?? ""
                );
                const endTimeList =
                  startTimeIndex > -1
                    ? timeOptions.slice(startTimeIndex + 1)
                    : [];

                return (
                  <div
                    className="flex items-center gap-3"
                    key={businessHour.index}
                  >
                    <FormField
                      control={form.control}
                      name={`businessHours.${businessHour.index}.openTime`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="sr-only">Start time</FormLabel>
                          <FormControl>
                            <SelectCombobox
                              className="w-32"
                              onChange={(value: string | null) => {
                                field.onChange(value);

                                const selectedStartTimeIndex =
                                  timeOptions.indexOf(value ?? "");
                                const endTimeIndex = timeOptions.indexOf(
                                  endTimeWatch ?? ""
                                );
                                if (selectedStartTimeIndex > endTimeIndex) {
                                  form.setValue(
                                    `businessHours.${businessHour.index}.closeTime`,
                                    timeOptions[selectedStartTimeIndex + 1] ??
                                      null
                                  );
                                }
                              }}
                              options={timeOptions}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Minus className="text-foreground size-5" />
                    <FormField
                      control={form.control}
                      name={`businessHours.${businessHour.index}.closeTime`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="sr-only">End time</FormLabel>
                          <FormControl>
                            <SelectCombobox
                              className="w-32"
                              onChange={field.onChange}
                              options={endTimeList}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {i === 0 ? (
                      <button
                        onClick={() => {
                          append({
                            openTime: "12:00am",
                            closeTime: "12:00am",
                            dayOfTheWeek: day,
                          });
                        }}
                        type="button"
                      >
                        <Plus className="text-foreground size-5" />
                        <span className="sr-only">
                          Add new time period on {day}
                        </span>
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          const currentValues = form.getValues("businessHours");
                          form.setValue(
                            "businessHours",
                            currentValues.filter(
                              (_, index) => index !== businessHour.index
                            )
                          );
                        }}
                        type="button"
                      >
                        <Trash2 className="text-destructive size-5" />
                        <span className="sr-only">Delete this time period</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {errorMessage && (
        <p className="body-sm text-destructive text-center">{errorMessage}</p>
      )}
    </div>
  );
}
